import React, { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { LazyFrameImage } from './LazyFrameImage';
import { StatusBadge } from './StatusBadge';

/**
 * Analysis takeaway row: left accent stripe, badge, copy, tall video-style thumbnail.
 */
export function TakeawayCard({ takeaway, linkFramesByClipId = {}, to }) {
  const isStrength = takeaway.category === 'strength';
  const isPositiveScore = Number(takeaway.score ?? 50) >= 60;
  const stripe = isPositiveScore ? 'bg-beachvision-teal' : 'bg-beachvision-coral';
  const clipCount = takeaway.point_clips?.length || 0;
  const scrollerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const frames = useMemo(() => {
    const clips = takeaway.point_clips || [];
    if (!clips.length) {
      return [{ key: `takeaway-${takeaway.id}`, mode: 'takeaway' }];
    }
    return clips.map((clip, idx) => ({
      key: `${clip.point_id || takeaway.id}-${clip.best_frame_ms || clip.clip_start_ms || idx}`,
      mode: 'point',
      frameImageUrl: linkFramesByClipId[String(clip.id)] || null,
    }));
  }, [takeaway, linkFramesByClipId]);

  const handleScroll = () => {
    const node = scrollerRef.current;
    if (!node) return;
    const slideWidth = node.clientWidth || 1;
    const idx = Math.round(node.scrollLeft / slideWidth);
    setActiveIndex(Math.max(0, Math.min(idx, frames.length - 1)));
  };

  return (
    <Link
      to={to}
      className="w-full text-left rounded-2xl bg-beachvision-surface-low flex overflow-hidden min-h-[112px] hover:brightness-110 transition-[filter] border border-white/5"
    >
      <span className={`w-1 shrink-0 ${stripe}`} aria-hidden />
      <div className="flex flex-1 min-w-0 items-stretch">
        <div className="flex-1 min-w-0 p-4 flex flex-col justify-center gap-2">
          <StatusBadge variant={isStrength ? 'strength' : 'needs_work'} outline>
            {isStrength ? 'Strength' : 'Needs work'}
          </StatusBadge>
          <h3 className="font-display font-bold text-white text-base leading-snug line-clamp-2">
            {takeaway.title}
          </h3>
          <p className="text-sm text-beachvision-muted line-clamp-2 leading-relaxed">
            {takeaway.description}
          </p>
          <p className="text-[11px] uppercase tracking-wide text-beachvision-muted font-display font-bold">
            {clipCount} {clipCount === 1 ? 'clip' : 'clips'}
          </p>
        </div>
        <div className="relative w-[30%] max-w-[120px] shrink-0">
          <div
            ref={scrollerRef}
            onScroll={handleScroll}
            className="absolute inset-0 h-full w-full rounded-r-2xl overflow-x-auto overflow-y-hidden flex snap-x snap-mandatory scroll-smooth"
          >
            {frames.map((frame) => (
              <div key={frame.key} className="relative h-full w-full shrink-0 snap-start overflow-hidden">
                <LazyFrameImage
                  className="h-full w-full"
                  loadImage={() => Promise.resolve(frame.frameImageUrl)}
                  frameUnavailable={frame.mode === 'point' && !frame.frameImageUrl}
                  placeholderVariant={frame.mode === 'takeaway' ? 'neutral' : 'expected'}
                  alt={`${takeaway.title} frame`}
                />
              </div>
            ))}
          </div>
          <div className="absolute inset-0 bg-gradient-to-l from-black/50 to-transparent rounded-r-2xl pointer-events-none" />
          {frames.length > 1 ? (
            <div className="absolute bottom-1.5 inset-x-0 flex items-center justify-center gap-1 pointer-events-none">
              {frames.map((frame, idx) => (
                <span
                  key={`dot-${frame.key}`}
                  className={`h-1 rounded-full transition-all ${
                    idx === activeIndex ? 'w-3 bg-white/90' : 'w-1 bg-white/40'
                  }`}
                />
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </Link>
  );
}
